/**
 * @fileoverview What the author is told after publishing a site.
 *
 * THE SAME RULE AS THE PAGE'S OWN RESULT NOTICE, and a separate component rather
 * than a shared one because the outcomes are different values with different
 * cases. Sharing a component here would mean a union of two publishers' outcome
 * types and a branch for every case the other one has, which is more coupling
 * than the eight lines it would save.
 *
 * EVERY OUTCOME IS SPELLED OUT AND THEY STAY DISTINCT. This is the deliberate
 * opposite of the read path, which collapses every failure into one
 * indistinguishable "not available" so a private page cannot be told from a
 * missing one. Nobody's privacy is served by being vague with an author about
 * their own site; it just leaves them pressing the same button.
 *
 * EVERY FAILURE SAYS TWO THINGS: what happened, and that their work is still in
 * front of them. An author who thinks a refusal ate the list they just arranged
 * will close the tab, which is the one action that actually loses it.
 */

import { Link } from 'react-router-dom';

import type { CollectionPublishOutcome } from '@/services/pages/publishCollection';

/** Bytes as something a person can compare: "412 KB", not "421,888". */
function readableSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} bytes`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function CollectionPublishResult({ outcome }: { outcome: CollectionPublishOutcome }) {
  if (outcome.status === 'published') {
    return (
      <div className="editor-result editor-result-good" role="status">
        <p>
          Published. Its address is permanent, so this link keeps working after every later
          edit. <Link to={`/${outcome.naddr}`}>Open the site</Link>.
        </p>
        <p className="editor-result-detail">
          Accepted by {outcome.acceptedBy.length === 1 ? 'one relay' : `${outcome.acceptedBy.length} relays`}.
        </p>
      </div>
    );
  }

  if (outcome.status === 'too-large') {
    return (
      <div className="editor-result editor-result-bad" role="alert">
        <p>
          <strong>This site is too large for the relay to accept.</strong> It came to{' '}
          {readableSize(outcome.bytes)} and {outcome.relay} takes at most{' '}
          {readableSize(outcome.limit)}.
        </p>
        <p className="editor-result-detail">
          Nothing was sent and nothing is lost. A shorter summary is usually the fastest way
          down, because it is carried twice: once as a tag and once as the text relay search
          reads.
          {!outcome.advertised &&
            ' That relay does not publish its limit, so this is the figure we assume rather than one it told us.'}
        </p>
      </div>
    );
  }

  if (outcome.status === 'invalid-name') {
    return (
      <div className="editor-result editor-result-bad" role="alert">
        <p>
          <strong>That address cannot be part of a link.</strong> Lowercase letters, numbers and
          hyphens only.
        </p>
        <p className="editor-result-detail">Nothing was sent. Your list is still here.</p>
      </div>
    );
  }

  if (outcome.status === 'key-required') {
    // SHOULD BE UNREACHABLE FROM THIS EDITOR, and is handled anyway. The route
    // recovers the key before the form opens precisely so nobody arranges a site
    // they cannot save. If this ever appears, that guarantee has broken, and the
    // author is owed the real reason rather than a generic failure.
    return (
      <div className="editor-result editor-result-bad" role="alert">
        <p>
          <strong>This site cannot be saved without its key.</strong> Saving it under a new one
          would quietly cut off everybody you had shared it with, so it was refused instead.
        </p>
        <p className="editor-result-detail">
          Nothing was sent. Reopening the site from its own address recovers the key.
        </p>
      </div>
    );
  }

  if (outcome.status === 'name-taken') {
    return (
      <div className="editor-result editor-result-bad" role="alert">
        <p>
          <strong>You already have a site at {outcome.identifier}.</strong> Saving this one there
          would replace its list and lock out everybody you had shared it with, so it was refused.
        </p>
        <p className="editor-result-detail">
          Nothing was sent. Give this site a different address, or open the existing one to edit
          it.
        </p>
      </div>
    );
  }

  if (outcome.status === 'name-unchecked') {
    return (
      <div className="editor-result editor-result-bad" role="alert">
        <p>
          <strong>Not saved: a relay did not answer.</strong> Without its answer there is no way
          to know whether you already have a site at {outcome.identifier}, and saving over one
          would lock out everybody you had shared it with.
        </p>
        <ul className="editor-result-detail">
          {outcome.relays.map((relay) => (
            <li key={relay}>{relay}</li>
          ))}
        </ul>
        <p className="editor-result-detail">Nothing was sent. Your list is still here.</p>
      </div>
    );
  }

  if (outcome.status === 'signer-failed') {
    return (
      <div className="editor-result editor-result-bad" role="alert">
        <p>
          <strong>Your signer refused.</strong> {outcome.message}
        </p>
        <p className="editor-result-detail">
          Nothing was sent. On a signer running on your phone this usually means the request
          never got an answer.
        </p>
      </div>
    );
  }

  const reasons = Object.entries(outcome.reasons);
  return (
    <div className="editor-result editor-result-bad" role="alert">
      <p>
        <strong>No relay accepted it.</strong>
      </p>
      {reasons.length === 0 ? (
        <p className="editor-result-detail">
          None of them said why, which usually means the connection closed rather than the site
          being refused.
        </p>
      ) : (
        <ul className="editor-result-detail">
          {reasons.map(([relay, reason]) => (
            <li key={relay}>
              {relay}: {reason}
            </li>
          ))}
        </ul>
      )}
      <p className="editor-result-detail">Your list is still here.</p>
    </div>
  );
}
